/**
 * Scan Bills Modal — 3-step AI bill detection wizard
 *
 * Step 1: Scan options (accounts + date range)
 * Step 2: Review & select detected bills
 * Step 3: Create bills and show summary
 */

import React, { useEffect, useRef, useState } from 'react';
import { X, Sparkles } from 'lucide-react';
import type { DateRange } from '../../../components/transactions/TransactionDatePicker';
import type {
  CreateBillsFromScanResult,
  DetectedBillCandidate,
} from '../../../types/bill-scan.types';
import { useCreateBillsFromScan, useScanForBills } from '../../../hooks/useBillScan';
import {
  getDefaultScanDateRange,
  ScanBillsStep1Options,
} from './ScanBillsStep1Options';
import { ScanBillsStep2Review } from './ScanBillsStep2Review';
import { ScanBillsStep3Create } from './ScanBillsStep3Create';

interface ScanBillsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete?: () => void;
}

const STEPS = [
  { num: 1, label: 'Scan options' },
  { num: 2, label: 'Select bills' },
  { num: 3, label: 'Create' },
];

export function ScanBillsModal({
  isOpen,
  onClose,
  onComplete,
}: ScanBillsModalProps): React.ReactElement {
  const [currentStep, setCurrentStep] = useState(1);
  const [accountIds, setAccountIds] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<DateRange>(getDefaultScanDateRange);
  const [candidates, setCandidates] = useState<DetectedBillCandidate[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [scanError, setScanError] = useState<string | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createResult, setCreateResult] = useState<CreateBillsFromScanResult | null>(null);
  const contentScrollRef = useRef<HTMLDivElement>(null);

  const scanMutation = useScanForBills();
  const createMutation = useCreateBillsFromScan();

  useEffect(() => {
    const el = contentScrollRef.current;
    if (el && typeof el.scrollTo === 'function') {
      el.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentStep]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const resetModal = () => {
    setCurrentStep(1);
    setAccountIds([]);
    setDateRange(getDefaultScanDateRange());
    setCandidates([]);
    setSelectedIds(new Set());
    setScanError(null);
    setCreateError(null);
    setCreateResult(null);
    scanMutation.reset();
    createMutation.reset();
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  const handleDone = () => {
    if (createResult && createResult.created > 0) {
      onComplete?.();
    }
    handleClose();
  };

  const handleScan = async () => {
    setScanError(null);
    try {
      const result = await scanMutation.mutateAsync({
        dateFrom: dateRange.from,
        dateTo: dateRange.to,
        accountIds: accountIds.length > 0 ? accountIds : undefined,
      });
      setCandidates(result);
      // Pre-select all non-existing candidates
      const initial = new Set(
        result.filter((c) => !c.alreadyExists).map((c) => c.id)
      );
      setSelectedIds(initial);
      setCurrentStep(2);
    } catch (e: unknown) {
      const err = e as Error & { code?: string };
      setScanError(err.message || 'Failed to scan transactions');
    }
  };

  const handleToggle = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleToggleAll = (selectAll: boolean) => {
    if (selectAll) {
      setSelectedIds(
        new Set(candidates.filter((c) => !c.alreadyExists).map((c) => c.id))
      );
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleCreate = async () => {
    setCreateError(null);
    setCreateResult(null);
    setCurrentStep(3);
    const selected = candidates.filter((c) => selectedIds.has(c.id) && !c.alreadyExists);
    try {
      const result = await createMutation.mutateAsync(selected);
      setCreateResult(result);
    } catch (e: unknown) {
      const err = e as Error;
      setCreateError(err.message || 'Failed to create bills');
    }
  };

  if (!isOpen) return <></>;

  return (
    <div
      className="fixed inset-0 z-[70]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="scan-bills-modal-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity z-[70]"
        onClick={handleClose}
        aria-hidden="true"
      />

      <div className="flex min-h-full items-start justify-center pt-14 pb-4 px-4 relative z-[71]">
        <div
          className="relative w-full max-w-4xl transform overflow-hidden rounded-lg bg-white shadow-xl transition-all dark:bg-gray-800 flex flex-col max-h-[calc(100vh-5rem)]"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex flex-1 overflow-hidden min-h-0">
            {/* Left sidebar stepper */}
            <div className="hidden sm:block w-48 border-r border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 flex-shrink-0">
              <div className="p-6">
                <div className="flex flex-col space-y-6">
                  {STEPS.map((step, index) => (
                    <div key={step.num} className="flex items-start space-x-3">
                      <div className="flex flex-col items-center flex-shrink-0">
                        <div
                          className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                            currentStep >= step.num
                              ? 'bg-primary-600 text-white'
                              : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                          }`}
                        >
                          {step.num}
                        </div>
                        {index < STEPS.length - 1 && (
                          <div
                            className={`w-0.5 flex-1 mt-2 transition-colors ${
                              currentStep > step.num
                                ? 'bg-primary-600'
                                : 'bg-gray-200 dark:bg-gray-700'
                            }`}
                            style={{ minHeight: '2rem' }}
                          />
                        )}
                      </div>
                      <div className="flex-1 pt-2">
                        <span
                          className={`text-sm font-medium block ${
                            currentStep >= step.num
                              ? 'text-primary-600 dark:text-primary-400'
                              : 'text-gray-500 dark:text-gray-400'
                          }`}
                        >
                          {step.label}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Main content */}
            <div className="flex-1 flex flex-col overflow-hidden min-h-0">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center flex-shrink-0">
                      <Sparkles className="h-5 w-5 text-violet-600 dark:text-violet-400" />
                    </div>
                    <div>
                      <h2
                        id="scan-bills-modal-title"
                        className="text-xl font-semibold text-gray-900 dark:text-gray-100"
                      >
                        Scan for Bills
                      </h2>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                        Use AI to find recurring bills in your transactions
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleClose}
                    className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                    aria-label="Close modal"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                {/* Mobile step indicator */}
                <div className="sm:hidden mt-3 flex items-center gap-2">
                  {STEPS.map((step) => (
                    <div
                      key={step.num}
                      className={`h-1.5 flex-1 rounded-full ${
                        currentStep >= step.num
                          ? 'bg-primary-600'
                          : 'bg-gray-200 dark:bg-gray-700'
                      }`}
                      aria-hidden="true"
                    />
                  ))}
                </div>
              </div>

              <div
                ref={contentScrollRef}
                className="flex-1 overflow-y-auto px-6 py-5"
              >
                {currentStep === 1 && (
                  <ScanBillsStep1Options
                    accountIds={accountIds}
                    onAccountIdsChange={setAccountIds}
                    dateRange={dateRange}
                    onDateRangeChange={setDateRange}
                    onScan={handleScan}
                    scanning={scanMutation.isPending}
                    error={scanError}
                  />
                )}
                {currentStep === 2 && (
                  <ScanBillsStep2Review
                    candidates={candidates}
                    selectedIds={selectedIds}
                    onToggle={handleToggle}
                    onToggleAll={handleToggleAll}
                    onBack={() => {
                      setScanError(null);
                      setCurrentStep(1);
                    }}
                    onNext={handleCreate}
                  />
                )}
                {currentStep === 3 && (
                  <ScanBillsStep3Create
                    creating={createMutation.isPending}
                    result={createResult}
                    error={createError}
                    onClose={handleDone}
                    onBack={() => setCurrentStep(2)}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
